import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { UserState } from '@/entities/subscription';
import ApiV2 from '@/adapters/api-v2';

const IMPERSONATION_STORAGE_KEY = 'eos_impersonate_email';

export interface UserContextValue {
  userState?: UserState;
  setUserState?(value: UserState | ((prevState: UserState) => UserState)): void;
  isLoadingOrgContext?: boolean;
  // Impersonation (super_admin only)
  startImpersonation?(email: string): Promise<void>;
  stopImpersonation?(): void;
  isImpersonating?: boolean;
}

export const UserContext = React.createContext<UserContextValue>({});

export interface UserProviderProps {
  children: React.ReactNode;
  userInitialState: UserState;
}

interface OrgContextResponse {
  success: boolean;
  data?: {
    userId: string;
    orgId: string;
    orgName: string;
    role: string;
    email: string;
    defaultEventId?: string;
    eventsManaged?: string[];
    impersonatedBy?: {
      userId: string;
      email: string;
    };
  };
  error?: string;
}

/**
 * Fetches organization context from eos-app
 *
 * @reason Gets user's org and default event info after Auth0 login
 * to enable org-scoped features
 */
async function fetchOrgContext(): Promise<OrgContextResponse | null> {
  try {
    const response = await ApiV2.get<OrgContextResponse>('/api/auth/me');
    return response;
  } catch (error) {
    console.error('Failed to fetch org context:', error);
    return null;
  }
}

export const UserProvider: React.FC<UserProviderProps> = ({
  children,
  userInitialState,
}: UserProviderProps) => {
  const [userState, setUserState] = useState<UserState>(userInitialState);
  const [isLoadingOrgContext, setIsLoadingOrgContext] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const { isAuthenticated, user, getAccessTokenSilently } = useAuth0();

  // Store original user state before impersonation (for super_admin restoration)
  const originalUserStateRef = useRef<UserState | null>(null);

  /**
   * Refetches org context, used after starting/stopping impersonation
   */
  const refetchOrgContext = useCallback(async (): Promise<void> => {
    setIsLoadingOrgContext(true);
    try {
      await getAccessTokenSilently();
      await new Promise((resolve) => setTimeout(resolve, 100));

      const response = await fetchOrgContext();
      if (response?.success && response.data) {
        const { orgId, orgName, defaultEventId, role, eventsManaged, impersonatedBy } =
          response.data;

        ApiV2.setOrgId(orgId);

        setUserState((prev) => ({
          ...prev,
          orgId,
          orgName,
          eventId: defaultEventId,
          role,
          eventsManaged,
          impersonatedBy,
        }));

        setIsImpersonating(!!impersonatedBy);
      }
    } catch (error) {
      console.warn('Failed to refetch org context:', error);
    } finally {
      setIsLoadingOrgContext(false);
    }
  }, [getAccessTokenSilently]);

  /**
   * Starts impersonating another user (super_admin only)
   */
  const startImpersonation = useCallback(
    async (email: string): Promise<void> => {
      // Store original state before impersonation
      if (!originalUserStateRef.current) {
        originalUserStateRef.current = { ...userState };
      }

      // Set impersonation email in API client
      ApiV2.setImpersonateEmail(email);
      localStorage.setItem(IMPERSONATION_STORAGE_KEY, email);

      // Refetch org context with impersonation
      await refetchOrgContext();
    },
    [userState, refetchOrgContext],
  );

  /**
   * Stops impersonating and restores original super_admin context
   */
  const stopImpersonation = useCallback((): void => {
    // Clear impersonation
    ApiV2.setImpersonateEmail(null);
    localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    setIsImpersonating(false);

    // Restore original state
    if (originalUserStateRef.current) {
      ApiV2.setOrgId(originalUserStateRef.current.orgId || '');
      setUserState((prev) => ({
        ...prev,
        ...originalUserStateRef.current,
        impersonatedBy: undefined,
      }));
      originalUserStateRef.current = null;
    }
  }, []);

  /**
   * Fetches organization context when user is authenticated
   *
   * @reason After Auth0 login, we need to get the user's org and event info
   * from eos-app to enable org-scoped features
   */
  useEffect(() => {
    // Only fetch if user is authenticated and we don't have org context yet
    if (isAuthenticated && user?.email && !userState.orgId) {
      // Check for existing impersonation session
      const savedImpersonateEmail = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
      if (savedImpersonateEmail) {
        ApiV2.setImpersonateEmail(savedImpersonateEmail);
      }

      setIsLoadingOrgContext(true);

      // Wait a bit to ensure App.tsx has set up auth headers
      const fetchWithDelay = async (): Promise<void> => {
        try {
          // This ensures auth tokens are ready
          await getAccessTokenSilently();

          // Small delay to ensure interceptor is registered
          await new Promise((resolve) => setTimeout(resolve, 100));

          const response = await fetchOrgContext();
          if (response?.success && response.data) {
            const { orgId, orgName, defaultEventId, role, eventsManaged, impersonatedBy } =
              response.data;

            // Set orgId in API client for all subsequent requests
            ApiV2.setOrgId(orgId);

            setUserState((prev) => ({
              ...prev,
              orgId,
              orgName,
              eventId: defaultEventId,
              role, // Update role from org membership
              eventsManaged, // Store managed event IDs for managers
              impersonatedBy,
            }));

            setIsImpersonating(!!impersonatedBy);
          }
        } catch (error) {
          console.warn('Org context not available, user may not be in an organization yet:', error);
        } finally {
          setIsLoadingOrgContext(false);
        }
      };

      void fetchWithDelay();
    }
  }, [isAuthenticated, user?.email, userState.orgId, getAccessTokenSilently]);

  return (
    <UserContext.Provider
      value={{
        userState,
        setUserState,
        isLoadingOrgContext,
        startImpersonation,
        stopImpersonation,
        isImpersonating,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
